import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const authHeaders = {
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

const INDIA_CENTER = [22.5, 79];
const DEFAULT_RADIUS_KM = 15;
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const TYPE_LABEL = {
  mechanic: 'Mechanic',
  towing: 'Towing',
  both: 'Mech + Towing',
};

const SPEC_FILTERS = [
  { id: 'all', label: 'All specs' },
  { id: 'Jump Start', label: 'Jump Start' },
  { id: 'Towing', label: 'Towing' },
  { id: 'Flat Tyre', label: 'Flat Tyre' },
  { id: 'Fuel Delivery', label: 'Fuel Delivery' },
  { id: 'Key Lockout', label: 'Key Lockout' },
  { id: 'Minor Repair', label: 'Minor Repair' },
];

const partnerSpecText = (row) =>
  [row.service_tag, row.service_tag2, row.service_tag3].filter(Boolean).join(', ');

const normalizeSpecs = (raw) => {
  const out = [];
  String(raw || '')
    .split(/[,·|/]/)
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((tag) => {
      if (SPEC_FILTERS.some((s) => s.id === tag) && !out.includes(tag)) {
        out.push(tag);
        return;
      }
      const lower = tag.toLowerCase();
      let mapped = null;
      if (lower.includes('fuel') || lower.includes('petrol') || lower.includes('diesel')) {
        mapped = 'Fuel Delivery';
      } else if (lower.includes('jump') || lower.includes('battery')) {
        mapped = 'Jump Start';
      } else if (
        lower.includes('punct') ||
        lower.includes('flat') ||
        lower.includes('tyre') ||
        lower.includes('tire')
      ) {
        mapped = 'Flat Tyre';
      } else if (lower.includes('tow')) {
        mapped = 'Towing';
      } else if (lower.includes('lock') || lower.includes('key')) {
        mapped = 'Key Lockout';
      } else if (lower.includes('minor') || lower.includes('repair') || lower.includes('break')) {
        mapped = 'Minor Repair';
      }
      if (mapped && !out.includes(mapped)) out.push(mapped);
    });
  return out;
};

const matchesSpec = (row, specId) => {
  if (!specId || specId === 'all') return true;
  return normalizeSpecs(partnerSpecText(row)).includes(specId);
};

const parseLatLng = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return [a, b];
};

const partnerName = (row) =>
  String(row.shop_name || row.mechanic_name || row.code || 'Partner').trim() || 'Partner';

const loadLeaflet = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.L) return Promise.resolve(window.L);
  if (window.__roadserveLeafletPromise) return window.__roadserveLeafletPromise;

  window.__roadserveLeafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => {
      if (window.L) resolve(window.L);
      else reject(new Error('Leaflet failed to load'));
    };
    script.onerror = () => reject(new Error('Leaflet script blocked'));
    document.body.appendChild(script);
  });
  return window.__roadserveLeafletPromise;
};

const fetchAllWithCoords = async () => {
  const rows = [];
  let offset = 0;
  const limit = 1000;
  while (true) {
    const params = new URLSearchParams();
    params.set(
      'select',
      'id,code,mechanic_name,shop_name,number,city,area,partner_type,active,owner_user_id,kyc_status,service_tag,service_tag2,service_tag3,service_center_lat,service_center_lng,service_radius_km',
    );
    params.set('service_center_lat', 'not.is.null');
    params.set('service_center_lng', 'not.is.null');
    params.set('order', 'mechanic_name.asc.nullslast');
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    const res = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?${params}`, { headers: authHeaders });
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) break;
    rows.push(...data);
    if (data.length < limit) break;
    offset += limit;
  }
  return rows.filter((row) => parseLatLng(row.service_center_lat, row.service_center_lng));
};

const countLinkedMissingGps = async () => {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/mechanics?select=id&owner_user_id=not.is.null&or=(service_center_lat.is.null,service_center_lng.is.null)&limit=1`,
      { headers: { ...authHeaders, Prefer: 'count=exact' } },
    );
    const header = res.headers.get('content-range') || '';
    const m = header.match(/\/(\d+)$/);
    return m ? Number(m[1]) || 0 : 0;
  } catch {
    return 0;
  }
};

const fetchRecentJobs = async () => {
  try {
    const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await serviceRoleClient
      .from('service_requests')
      .select('id, pickup_latitude, pickup_longitude, pickup_address, created_at, request_status')
      .not('pickup_latitude', 'is', null)
      .not('pickup_longitude', 'is', null)
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(200);
    if (error || !Array.isArray(data)) return [];
    return data.filter((row) => parseLatLng(row.pickup_latitude, row.pickup_longitude));
  } catch {
    return [];
  }
};

const fetchRecentBellCounts = async () => {
  const counts = new Map();
  try {
    const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await serviceRoleClient
      .from('partner_job_bell_targets')
      .select('mechanic_id, bell_started_at')
      .gte('bell_started_at', since)
      .limit(4000);
    if (error || !Array.isArray(data)) return counts;
    data.forEach((row) => {
      const id = String(row.mechanic_id || '');
      if (!id) return;
      counts.set(id, (counts.get(id) || 0) + 1);
    });
  } catch {
    /* table may be missing / RLS */
  }
  return counts;
};

const styleFor = (row, ringCount) => {
  const linked = Boolean(row.owner_user_id);
  const active = row.active === true;
  if (linked && active) {
    return {
      color: ringCount > 0 ? '#4f46e5' : '#059669',
      fill: ringCount > 0 ? '#6366f1' : '#10b981',
      label: ringCount > 0 ? 'Linked · rings going' : 'Linked · can receive rings',
    };
  }
  if (linked) {
    return { color: '#d97706', fill: '#f59e0b', label: 'Linked · inactive' };
  }
  return { color: '#64748b', fill: '#94a3b8', label: 'Not linked · no app login' };
};

const PartnerCoverageMap = ({ open, onClose }) => {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef(new Map());
  const partnersLayerRef = useRef(null);
  const jobsLayerRef = useRef(null);
  const drawGenRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partners, setPartners] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [bellCounts, setBellCounts] = useState(new Map());
  const [missingGps, setMissingGps] = useState(0);
  const [filterActive, setFilterActive] = useState('active');
  const [filterLinked, setFilterLinked] = useState('linked');
  const [filterKyc, setFilterKyc] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [specFilter, setSpecFilter] = useState('all');
  const [showJobs, setShowJobs] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return partners.filter((row) => {
      const linked = Boolean(row.owner_user_id);
      const active = row.active === true;
      const kycVerified = String(row.kyc_status || '').toLowerCase() === 'verified';
      if (filterActive === 'active' && !active) return false;
      if (filterActive === 'inactive' && active) return false;
      if (filterLinked === 'linked' && !linked) return false;
      if (filterLinked === 'unlinked' && linked) return false;
      if (filterKyc === 'verified' && !kycVerified) return false;
      if (filterKyc === 'unverified' && kycVerified) return false;
      if (typeFilter !== 'all' && String(row.partner_type || 'mechanic') !== typeFilter) return false;
      if (!matchesSpec(row, specFilter)) return false;
      if (!q) return true;
      const hay = [row.mechanic_name, row.shop_name, row.code, row.number, row.city, row.area]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [partners, filterActive, filterLinked, filterKyc, typeFilter, specFilter, search]);

  const stats = useMemo(() => {
    const linkedOnMap = partners.filter((r) => r.owner_user_id).length;
    const notifyOnMap = partners.filter((r) => r.owner_user_id && r.active === true).length;
    const ringPartners = visible.filter((r) => (bellCounts.get(String(r.id)) || 0) > 0).length;
    return { linkedOnMap, notifyOnMap, ringPartners, shown: visible.length };
  }, [partners, visible, bellCounts]);

  const destroyMap = () => {
    layersRef.current.clear();
    partnersLayerRef.current = null;
    jobsLayerRef.current = null;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rows, missing, recentJobs, rings] = await Promise.all([
        fetchAllWithCoords(),
        countLinkedMissingGps(),
        fetchRecentJobs(),
        fetchRecentBellCounts(),
      ]);
      setPartners(rows);
      setMissingGps(missing);
      setJobs(recentJobs);
      setBellCounts(rings);
    } catch (e) {
      setError(e.message || 'Could not load coverage');
    } finally {
      setLoading(false);
    }
  }, []);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    loadData();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      destroyMap();
    };
  }, [open, loadData]);

  useEffect(() => {
    if (!open || loading || !mapEl.current) return undefined;
    const drawId = ++drawGenRef.current;

    const draw = async () => {
      try {
        const L = await loadLeaflet();
        if (drawId !== drawGenRef.current || !mapEl.current) return;
        if (!mapRef.current) {
          mapRef.current = L.map(mapEl.current, {
            zoomControl: true,
            attributionControl: true,
          }).setView(INDIA_CENTER, 5);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap',
          }).addTo(mapRef.current);
        }

        const map = mapRef.current;
        if (partnersLayerRef.current) {
          partnersLayerRef.current.clearLayers();
        } else {
          partnersLayerRef.current = L.layerGroup().addTo(map);
        }
        layersRef.current.clear();
        if (jobsLayerRef.current) {
          jobsLayerRef.current.clearLayers();
        } else {
          jobsLayerRef.current = L.layerGroup().addTo(map);
        }

        const bounds = [];
        visible.forEach((row) => {
          const latlng = parseLatLng(row.service_center_lat, row.service_center_lng);
          if (!latlng) return;
          const rings = bellCounts.get(String(row.id)) || 0;
          const style = styleFor(row, rings);
          const km = Number(row.service_radius_km) > 0 ? Number(row.service_radius_km) : DEFAULT_RADIUS_KM;
          const group = L.layerGroup();
          L.circle(latlng, {
            radius: km * 1000,
            color: style.color,
            weight: 1.2,
            fillColor: style.fill,
            fillOpacity: 0.1,
          }).addTo(group);
          const marker = L.circleMarker(latlng, {
            radius: rings > 0 ? 8 : 6,
            color: '#fff',
            weight: 1.5,
            fillColor: style.fill,
            fillOpacity: 1,
          }).addTo(group);
          marker.bindPopup(
            `<div style="min-width:180px">
              <div style="font-weight:700;font-size:13px;margin-bottom:4px">${partnerName(row)}</div>
              <div style="font-size:11px;color:#475569;margin-bottom:6px">${style.label}</div>
              <div style="font-size:12px;line-height:1.45">
                ${row.code ? `<div><b>${row.code}</b></div>` : ''}
                <div>${TYPE_LABEL[row.partner_type] || 'Mechanic'}</div>
                <div>Specs: <b>${normalizeSpecs(partnerSpecText(row)).join(', ') || '—'}</b></div>
                <div>KYC: <b>${String(row.kyc_status || '').toLowerCase() === 'verified' ? 'Verified' : 'Unverified'}</b></div>
                <div>${[row.city, row.area].filter(Boolean).join(' · ') || 'City not set'}</div>
                <div>Notify radius: <b>${km} km</b></div>
                ${row.number ? `<div>${row.number}</div>` : ''}
                ${rings ? `<div style="margin-top:4px;color:#4f46e5"><b>${rings}</b> rings in last 14 days</div>` : ''}
              </div>
            </div>`,
          );
          marker.on('click', () => setSelectedId(row.id));
          group.addTo(partnersLayerRef.current);
          layersRef.current.set(row.id, { group, marker });
          bounds.push(latlng);
        });

        if (showJobs && jobs.length) {
          jobs.forEach((job) => {
            const latlng = parseLatLng(job.pickup_latitude, job.pickup_longitude);
            if (!latlng) return;
            L.circleMarker(latlng, {
              radius: 4,
              color: '#fff',
              weight: 1,
              fillColor: '#7c3aed',
              fillOpacity: 0.9,
            })
              .bindPopup(
                `<div style="min-width:160px">
                  <div style="font-weight:700;font-size:12px;margin-bottom:4px">Job pickup · notification sent here</div>
                  <div style="font-size:12px">${job.pickup_address || 'Location'}</div>
                  <div style="font-size:11px;color:#64748b;margin-top:4px">${
                    job.created_at ? new Date(job.created_at).toLocaleString('en-IN') : ''
                  }</div>
                </div>`,
              )
              .addTo(jobsLayerRef.current);
          });
        }

        if (drawId !== drawGenRef.current) return;
        if (bounds.length) {
          map.fitBounds(bounds, { padding: [28, 28], maxZoom: 12 });
        } else {
          map.setView(INDIA_CENTER, 5);
        }
        setTimeout(() => {
          if (drawId === drawGenRef.current && mapRef.current) mapRef.current.invalidateSize();
        }, 80);
      } catch (e) {
        if (drawId === drawGenRef.current) setError(e.message || 'Map failed to load');
      }
    };

    draw();
    return () => {
      drawGenRef.current += 1;
    };
  }, [open, loading, visible, jobs, showJobs, bellCounts]);

  const focusPartner = (row) => {
    setSelectedId(row.id);
    const entry = layersRef.current.get(row.id);
    const map = mapRef.current;
    const latlng = parseLatLng(row.service_center_lat, row.service_center_lng);
    if (!map || !latlng) return;
    map.flyTo(latlng, 12, { duration: 0.6 });
    if (entry?.marker) entry.marker.openPopup();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-5">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        aria-label="Close coverage map"
        onClick={onClose}
      />
      <div className="relative z-10 flex h-full max-h-[940px] w-full max-w-[1400px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
              Check coverage
            </p>
            <h3 className="text-lg font-semibold text-slate-900">Where linked partners get job rings</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Green pin = linked + active (notification jaata hai). Circle = unka service radius.
              Purple dots = last 14 days ke job pickups.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600 sm:block">
              <span className="font-semibold text-emerald-700">{stats.shown}</span> on map ·{' '}
              <span className="font-semibold text-indigo-700">{stats.ringPartners}</span> recent rings ·{' '}
              <span className="font-semibold text-amber-700">{missingGps}</span> linked, no GPS
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {['all', 'active', 'inactive'].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilterActive(key)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold capitalize ${
                  filterActive === key ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {key}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {[
              { id: 'all', label: 'All login' },
              { id: 'linked', label: 'Linked' },
              { id: 'unlinked', label: 'Not linked' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilterLinked(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  filterLinked === opt.id ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {[
              { id: 'all', label: 'All KYC' },
              { id: 'verified', label: 'Verified' },
              { id: 'unverified', label: 'Unverified' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilterKyc(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  filterKyc === opt.id ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {[
              { id: 'all', label: 'All types' },
              { id: 'mechanic', label: 'Mechanic' },
              { id: 'towing', label: 'Towing' },
              { id: 'both', label: 'Both' },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setTypeFilter(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                  typeFilter === opt.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
            <input
              type="checkbox"
              checked={showJobs}
              onChange={(e) => setShowJobs(e.target.checked)}
              className="rounded border-slate-300 text-indigo-600"
            />
            Job pickups ({jobs.length})
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Spec
          </span>
          {SPEC_FILTERS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setSpecFilter(opt.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                specFilter === opt.id
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1">
          <aside className="flex w-[300px] shrink-0 flex-col border-r border-slate-100 bg-slate-50/70">
            <div className="relative border-b border-slate-100 p-2.5">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find partner on map…"
                className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-xs text-slate-500">
                  <Loader2 size={14} className="animate-spin" />
                  Loading partners…
                </div>
              ) : visible.length === 0 ? (
                <p className="px-3 py-8 text-center text-xs text-slate-500">
                  No partners with GPS in this filter.
                </p>
              ) : (
                visible.map((row) => {
                  const rings = bellCounts.get(String(row.id)) || 0;
                  const style = styleFor(row, rings);
                  const kycVerified = String(row.kyc_status || '').toLowerCase() === 'verified';
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => focusPartner(row)}
                      className={`flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2.5 text-left hover:bg-white ${
                        selectedId === row.id ? 'bg-white ring-1 ring-inset ring-indigo-200' : ''
                      }`}
                    >
                      <span
                        className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: style.fill }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-semibold text-slate-900">
                          {partnerName(row)}
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                          {kycVerified ? 'KYC ✓' : 'Unverified'} ·{' '}
                          {normalizeSpecs(partnerSpecText(row)).join(', ') || 'No spec'} ·{' '}
                          {[row.city, row.area].filter(Boolean).join(' · ') || 'No city'} ·{' '}
                          {Number(row.service_radius_km) || DEFAULT_RADIUS_KM} km
                          {rings ? ` · ${rings} rings` : ''}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <div className="relative min-w-0 flex-1">
            {error ? (
              <div className="absolute inset-x-4 top-4 z-[500] rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            ) : null}
            <div ref={mapEl} className="h-full w-full bg-slate-100" />
            <div className="pointer-events-none absolute bottom-3 left-3 z-[500] rounded-lg border border-slate-200 bg-white/95 px-2.5 py-2 text-[10px] shadow-sm">
              <p className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Legend</p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Linked + active (notify)
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-indigo-500" /> Recent job rings
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-amber-500" /> Linked, inactive
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-slate-400" /> Not linked
              </p>
              <p className="flex items-center gap-1.5 text-slate-700">
                <span className="h-2 w-2 rounded-full bg-violet-600" /> Job pickup (last 14d)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerCoverageMap;
